import { useEffect, useState, useCallback } from "react";
import {
  listQueuedScans,
  removeQueuedScan,
  updateQueuedScan,
  queueSize,
  computeBackoffMs,
} from "./offline-queue";
import { recordCheckpointScan } from "./checkpoint-scans";
import { pendingFieldOps, pruneSyncedFieldOps } from "./offline-field-queue";
import { flushFieldQueue } from "./offline-field-sync";

export type SyncState = {
  online: boolean;
  /** Total pending items across scans, incidents and DAR memos. */
  pending: number;
  pendingScans: number;
  pendingIncidents: number;
  pendingMemos: number;
  syncing: boolean;
};

const MAX_ATTEMPTS = 12;

async function flushQueue(): Promise<{ flushed: number; failed: number }> {
  const items = await listQueuedScans();
  const now = Date.now();
  let flushed = 0, failed = 0;
  for (const item of items) {
    if (item.nextRetryAt && item.nextRetryAt > now) continue; // not eligible yet
    try {
      const result = await recordCheckpointScan({
        token: item.token,
        checkpointId: item.checkpointId,
        method: item.method === "gps_auto" ? "gps" : item.method,
        position: item.latitude != null && item.longitude != null
          ? ({ latitude: item.latitude, longitude: item.longitude, accuracy: item.accuracy ?? 0 } as GeolocationCoordinates)
          : null,
        note: item.note ?? null,
        clientId: item.id,           // server-side idempotency
        capturedAt: item.capturedAt,
        silentPanic: item.silentPanic,
      });
      if (result.ok) {
        await removeQueuedScan(item.id);
        flushed++;
      } else if (
        result.code === "not_found" ||
        result.code === "forbidden" ||
        result.code === "signature_invalid" ||
        result.code === "replay_blocked" ||
        result.code === "token_expired"
      ) {
        // Permanent failure — drop so we don't retry forever.
        await removeQueuedScan(item.id);
        failed++;
      } else {
        const attempts = item.attempts + 1;
        if (attempts >= MAX_ATTEMPTS) {
          await removeQueuedScan(item.id);
        } else {
          await updateQueuedScan({
            ...item,
            attempts,
            lastError: result.message,
            nextRetryAt: Date.now() + computeBackoffMs(attempts),
          });
        }
        failed++;
      }
    } catch (e: any) {
      const attempts = item.attempts + 1;
      await updateQueuedScan({
        ...item,
        attempts,
        lastError: e?.message,
        nextRetryAt: Date.now() + computeBackoffMs(attempts),
      });
      failed++;
      break; // network is flaky again; stop draining
    }
  }
  return { flushed, failed };
}

export function useOfflineSync(): SyncState & { syncNow: () => Promise<void> } {
  const [state, setState] = useState<SyncState>({
    online: typeof navigator === "undefined" ? true : navigator.onLine,
    pending: 0,
    pendingScans: 0,
    pendingIncidents: 0,
    pendingMemos: 0,
    syncing: false,
  });

  const refreshPending = useCallback(async () => {
    const scans = await queueSize().catch(() => 0);
    const ops = await pendingFieldOps().catch(() => []);
    const incidents = ops.filter((o) => o.kind === "incident").length;
    const memos = ops.filter((o) => o.kind === "dar_memo").length;
    setState((s) => ({
      ...s,
      pendingScans: scans,
      pendingIncidents: incidents,
      pendingMemos: memos,
      pending: scans + incidents + memos,
    }));
  }, []);

  const syncNow = useCallback(async () => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    setState((s) => ({ ...s, syncing: true }));
    await flushQueue().catch(() => undefined);
    await flushFieldQueue().catch(() => undefined);
    await pruneSyncedFieldOps().catch(() => undefined);
    await refreshPending();
    setState((s) => ({ ...s, syncing: false }));
  }, [refreshPending]);

  useEffect(() => {
    refreshPending();
    const onOnline = () => {
      setState((s) => ({ ...s, online: true }));
      void syncNow();
    };
    const onOffline = () => setState((s) => ({ ...s, online: false }));
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const i = setInterval(() => {
      void refreshPending();
      if (typeof navigator === "undefined" || navigator.onLine) void syncNow();
    }, 8000);
    if (state.online) void syncNow();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      clearInterval(i);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ...state, syncNow };
}
